import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { ComposedInvoiceActionsCell } from 'src/components/invoice/grid/ComposedInvoiceActionsCell'
import { ComposedInvoiceStatusCell } from 'src/components/invoice/grid/ComposedInvoiceStatusCell'
import type { ComposedInvoice, PendingInvoiceAction } from 'src/interfaces/invoice'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import { getComposedInvoiceLabel } from 'src/utils/invoiceLabels'
import {
  deriveComposedInvoiceTypes,
  isComposedInvoiceGenerated,
  sumPartnerInvoiceAmounts,
} from 'src/utils/invoiceStatus'
import { composedInvoiceTableHeaderTitles, partnerInvoiceTypeLabels } from './constants'
import { ComposedInvoicesTableFields } from './enums'

/**
 * Wiring only — every derived value comes from utils/invoiceStatus, money from
 * utils/invoiceMoney, actions from ComposedInvoiceActionsCell.
 *
 * EVERY column is `sortable: false`. `GridTable` defaults to `sortingMode="server"`, and the
 * list endpoint has no sort parameter at all (fixed `pci.createdAt DESC`) — an enabled header
 * would be a control that silently does nothing. Client-side sorting is equally wrong here: it
 * would reorder the current page only and misrepresent the other pages.
 *
 * A factory rather than a module constant: the actions column closes over the page's handler.
 * Callers must memoize the result, since a fresh array each render resets the grid's column
 * state.
 */
export const buildComposedInvoiceColumns = (
  onRunAction: (action: PendingInvoiceAction) => void,
  // `invoices_manage` (ENG-1207 part 2): every verb in the menu posts to an invoices_manage route,
  // so without it the actions column is dropped.
  canManage: boolean,
): Array<GridColDef<ComposedInvoice>> => {
  const columns: Array<GridColDef<ComposedInvoice>> = [
    {
      field: ComposedInvoicesTableFields.invoice,
      headerName: composedInvoiceTableHeaderTitles[ComposedInvoicesTableFields.invoice],
      flex: 2,
      minWidth: 200,
      sortable: false,
      // Identifies the row, so it is never offered for hiding in the manage-columns panel.
      hideable: false,
      // v8 renders cells in text mode by default; element children are only laid out (and
      // vertically centred) when the column opts into flex display.
      display: 'flex',
      renderCell: (params: GridRenderCellParams<ComposedInvoice>) => {
        return (
          <Typography variant="body2" css={tw`truncate`}>
            {getComposedInvoiceLabel(params.row)}
          </Typography>
        )
      },
    },
    {
      field: ComposedInvoicesTableFields.type,
      headerName: composedInvoiceTableHeaderTitles[ComposedInvoicesTableFields.type],
      width: 140,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<ComposedInvoice>) => {
        const types = deriveComposedInvoiceTypes(params.row)

        if (types.length === 0) {
          // A dash for a missing derived value is a cosmetic default, not fabricated content.
          return (
            <Typography variant="body2" css={tw`text-shades-500`}>
              —
            </Typography>
          )
        }

        return (
          <Typography variant="body2" css={tw`truncate`}>
            {types
              .map((type) => {
                return partnerInvoiceTypeLabels[type]
              })
              .join(', ')}
          </Typography>
        )
      },
    },
    {
      field: ComposedInvoicesTableFields.status,
      headerName: composedInvoiceTableHeaderTitles[ComposedInvoicesTableFields.status],
      width: 240,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<ComposedInvoice>) => {
        return <ComposedInvoiceStatusCell invoice={params.row} />
      },
    },
    {
      field: ComposedInvoicesTableFields.amount,
      headerName: composedInvoiceTableHeaderTitles[ComposedInvoicesTableFields.amount],
      width: 140,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<ComposedInvoice>) => {
        // Derived: the composed DTO carries no total, so it is summed from the nested rows.
        return (
          <Typography variant="body2">
            {formatCentsAsUsd(sumPartnerInvoiceAmounts(params.row))}
          </Typography>
        )
      },
    },
    {
      field: ComposedInvoicesTableFields.lateFee,
      headerName: composedInvoiceTableHeaderTitles[ComposedInvoicesTableFields.lateFee],
      width: 120,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<ComposedInvoice>) => {
        return <Typography variant="body2">{formatCentsAsUsd(params.row.lateFeeAmount)}</Typography>
      },
    },
    {
      field: ComposedInvoicesTableFields.generated,
      headerName: composedInvoiceTableHeaderTitles[ComposedInvoicesTableFields.generated],
      width: 150,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<ComposedInvoice>) => {
        // A yes/no indicator, not a link: there is no admin-side download endpoint. The only
        // download route in para-node is customer-authenticated and keyed to the caller's own
        // partnerId, so the admin token cannot reach it.
        return (
          <Typography variant="body2">
            {isComposedInvoiceGenerated(params.row) ? 'Yes' : 'No'}
          </Typography>
        )
      },
    },
    {
      field: ComposedInvoicesTableFields.actions,
      headerName: composedInvoiceTableHeaderTitles[ComposedInvoicesTableFields.actions],
      width: 68,
      sortable: false,
      hideable: false,
      disableExport: true,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<ComposedInvoice>) => {
        return <ComposedInvoiceActionsCell invoice={params.row} onRunAction={onRunAction} />
      },
    },
  ]

  if (canManage) {
    return columns
  }

  return columns.filter((column) => {
    return column.field !== ComposedInvoicesTableFields.actions
  })
}
